/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useQuery, useMutation } from 'react-query'

import { getSaasUserOktaGroups, enrollSaasUserInOktaGroup } from '@modules/okta-api'
import { queryClient } from '@modules/query'
import type { ApiErrorCollection } from '@modules/query/types'
import type { SaasUserOktaGroupsResponse } from '@modules/cloud-api/v1/types'

import type { UseQueryOptions } from 'react-query'

/**
 * Hook to fetch Okta groups for a specific user
 */
export const useGetSaasUserOktaGroups = (
  userId: string,
  options?: UseQueryOptions<unknown, ApiErrorCollection, SaasUserOktaGroupsResponse>,
) =>
  useQuery<unknown, ApiErrorCollection, SaasUserOktaGroupsResponse>(
    ['getSaasUserOktaGroups', userId],
    () => getSaasUserOktaGroups(userId),
    {
      ...options,
    },
  )

/**
 * Hook to enroll a user in an Okta group
 */
export const usePostSaasUserOktaGroups = () =>
  useMutation<unknown, ApiErrorCollection, { userId: string; groupName: string }>(
    ({ userId, groupName }) => enrollSaasUserInOktaGroup(userId, groupName),
    {
      onSuccess: (_, { userId }) => {
        // Invalidate the groups query to trigger a refetch
        queryClient.invalidateQueries(['getSaasUserOktaGroups', userId])
      },
    },
  )
